import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { SnackbarService } from '@core/services/snackbar.service';
import { UserProfileService } from '@core/services/user-profile/user-profile.service';
import { CustomerType } from '@license/invoice-details-form/customer-type.enum';
import { CustomSnackbarType } from '@shared/component/custom-snackbar/custom-snackbar.model';
import { UserDataType } from '@shared/component/my-account/model/user-data-type.enum';
import { UpdateProfilePayload, UserProfileAddress } from '@shared/model/user-profile.model';
import { findSelectedCountryByIso } from '@shared/sf-form-controls/sf-country-selector/sf-country-selector/country-selector.utils';
import { resolvePhonePrefix } from '@shared/utils/common.utils';
import { RegexPatterns } from '@shared/validators/regex-patterns.enum';
import { BehaviorSubject, catchError, EMPTY, Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

export enum UserDataFormControls {
	CustomerType = 'customerType',
	Address = 'address',
	City = 'city',
	PostCode = 'postCode',
	Phone = 'phone',
	CountryCode = 'countryCode',
	VatNumber = 'vatNumber',
	Company = 'company',
	PersonalIdNumber = 'personalIdNumber',
	LastName = 'lastName',
	FirstName = 'firstName',
}
@Component({
	selector: 'sf-user-data-modal',
	templateUrl: './user-data-modal.component.html',
	styleUrls: ['./user-data-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDataModalComponent implements OnInit, OnDestroy {
	public userDetailsForm: FormGroup;
	public userData: UserProfileAddress;
	public prefixText: BehaviorSubject<string> = new BehaviorSubject(null);
	public showVatError: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public individualCustomerTempData: any;
	public companyCustomerTempData: any;
	public buttonDisabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public customerType: typeof CustomerType = CustomerType;
	public userDataType: typeof UserDataType = UserDataType;
	public filteredCountries$: Observable<string[]>;
	public userDataFormControls: typeof UserDataFormControls = UserDataFormControls;
	private destroy: Subject<void> = new Subject();
	public get isCompanySelected(): boolean {
		return this.userDetailsForm.controls.customerType.value === CustomerType.COMPANY;
	}
	public get countryControl(): FormControl {
		return this.userDetailsForm.controls.countryCode as FormControl;
	}

	public get phoneControl(): FormControl {
		return this.userDetailsForm.controls.phone as FormControl;
	}
	public get personalIdControl(): FormControl {
		return this.userDetailsForm.controls.personalIdNumber as FormControl;
	}
	public get vatNumberControl(): FormControl {
		return this.userDetailsForm.controls.vatNumber as FormControl;
	}

	public get postCodeControl(): FormControl {
		return this.userDetailsForm.controls.postCode as FormControl;
	}
	constructor(
		private readonly fb: FormBuilder,
		private readonly snackBarService: SnackbarService,
		private readonly userProfileService: UserProfileService,
		private readonly dialogRef: MatDialogRef<UserDataModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {}

	public ngOnInit() {
		this.initForm();

		this.userDetailsForm.controls.countryCode.valueChanges
			.pipe(
				tap(() => {
					this.resolvePhonePrefix();
					this.resolveValidation();
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	public resolvePhonePrefix(): void {
		const country = findSelectedCountryByIso(this.countryControl.value);
		this.prefixText.next(country && country.countryCode ? '+' + country.countryCode : '');
		this.userDetailsForm.controls.prefix.setValue(this.prefixText.value);
	}
	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}
	private initForm(): void {
		this.data.dataType === UserDataType.PersonalData
			? (this.userData = this.data.formData.personalAddress)
			: (this.userData = this.data.formData.invoiceAddress
					? this.data.formData.invoiceAddress
					: this.data.formData.personalAddress);
		this.userDetailsForm = this.fb.group({
			customerType: new FormControl<string>(
				this.userData && this.userData.customerType ? this.userData.customerType : CustomerType.INDIVIDUAL,
				[Validators.required]
			),
			address: new FormControl<string>(null, [Validators.required, Validators.minLength(3), Validators.maxLength(40)]),
			city: new FormControl<string>(null, [Validators.required, Validators.maxLength(40)]),
			postCode: new FormControl<string>(null, [Validators.required, Validators.minLength(3), Validators.maxLength(40)]),
			phone: new FormControl<number>(null, [Validators.required]),
			countryCode: new FormControl<string>(null, [Validators.required]),
			prefix: new FormControl<string>(null),
		});

		this.resolveFormControls();
	}

	public onSaveClick(): void {
		this.userProfileService
			.updateProfile(this.resolvePayload())
			.pipe(
				catchError((error) => {
					if (error.status === 400) {
						this.showVatError.next(true);
					}
					return EMPTY;
				}),
				tap((response) => {
					this.dialogRef.close({
						dataUpdated: true,
						formData: response,
					});
					this.snackBarService.openSnackbar({
						message: `sf.shared_component.my_account.user_data.notifications.success`,
						type: CustomSnackbarType.Success,
					});
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	private resolvePayload(): UpdateProfilePayload {
		return this.data.dataType === UserDataType.InvoiceData
			? {
					digitalInvoice: true,
					personalAddress: {
						...this.data.formData.personalAddress,
						prefix: resolvePhonePrefix(this.data.formData.personalAddress.countryCode),
					},
					invoiceAddress: {
						...this.userDetailsForm.value,
						prefix: this.userDetailsForm.value.prefix || resolvePhonePrefix(this.userDetailsForm.value.countryCode),
					},
					invoiceAddressOtherThanPersonal: true,
				}
			: {
					digitalInvoice: true,
					personalAddress: {
						...this.userDetailsForm.value,
						prefix: this.userDetailsForm.value.prefix || resolvePhonePrefix(this.userDetailsForm.value.countryCode),
					},
				};
	}

	public resolvePreviousData(): void {
		this.isCompanySelected
			? (this.companyCustomerTempData = this.userDetailsForm.getRawValue())
			: (this.individualCustomerTempData = this.userDetailsForm.getRawValue());
	}

	public customerTypeChange(event: MatRadioChange): void {
		event.value === CustomerType.COMPANY
			? this.handleCompanyCustomerControls()
			: this.handleIndividualCustomerControls();
		const tempData =
			event.value === CustomerType.COMPANY ? this.companyCustomerTempData : this.individualCustomerTempData;
		this.userDetailsForm.patchValue(tempData);
		// Ensure prefix is visible after customer type change
		if (tempData && tempData.prefix) {
			this.prefixText.next(tempData.prefix);
		} else if (this.userDetailsForm.controls.countryCode.value) {
			this.resolvePhonePrefix();
		}
		this.resolveValidation();
	}

	private resolveFormControls(): void {
		this.userData && this.userData.customerType === CustomerType.COMPANY
			? this.handleCompanyCustomerControls()
			: this.handleIndividualCustomerControls();
		this.userDetailsForm.patchValue(this.userData);
		this.resolveValidation(this.isNewFormOrEdit());
		// Always ensure prefix is set
		if (this.userData && this.userData.countryCode) {
			if (this.userData.prefix && this.userData.prefix.trim() !== '') {
				// Set prefix from existing data
				this.prefixText.next(this.userData.prefix);
				this.userDetailsForm.controls.prefix.setValue(this.userData.prefix);
			} else {
				// Calculate prefix from country code if not set or empty
				this.resolvePhonePrefix();
			}
		} else {
			// No userData or no countryCode - resolve from form
			this.resolvePhonePrefix();
		}
	}

	private isNewFormOrEdit(): boolean {
		return Boolean(!this.userData);
	}

	private resolveValidation(isNewForm: boolean = false): void {
		if (this.countryControl.value === 'PL') {
			this.phoneControl.addValidators(Validators.pattern(RegexPatterns.EXACT_9_DIGITS_NUMBER));
			this.postCodeControl.addValidators(Validators.pattern(RegexPatterns.POST_CODE));
			if (this.isCompanySelected) {
				this.vatNumberControl?.setValidators([
					Validators.required,
					Validators.pattern(RegexPatterns.EXACT_10_DIGITS_NUMBER),
				]);
			} else {
				this.personalIdControl?.setValidators([
					Validators.required,
					Validators.pattern(RegexPatterns.EXACT_11_DIGITS_NUMBER),
				]);
			}
		} else {
			this.phoneControl.setValidators([
				Validators.required,
				Validators.pattern(RegexPatterns.MIN_3_AND_MAX_15_DIGITS_NUMBER),
			]);
			this.postCodeControl.setValidators([Validators.required, Validators.minLength(3), Validators.maxLength(20)]);
			if (this.isCompanySelected) {
				this.vatNumberControl?.setValidators([Validators.required, Validators.minLength(5), Validators.maxLength(20)]);
			} else {
				this.personalIdControl?.setValidators([Validators.minLength(3), Validators.maxLength(20)]);
			}
		}
		if (!isNewForm) {
			this.phoneControl.markAsTouched();
			this.postCodeControl.markAsTouched();
			if (this.isCompanySelected) {
				this.vatNumberControl?.markAsTouched();
			} else {
				this.personalIdControl?.markAsTouched();
			}
		}
		if (this.isCompanySelected) {
			this.vatNumberControl?.updateValueAndValidity();
		} else {
			this.personalIdControl?.updateValueAndValidity();
		}
		this.phoneControl.updateValueAndValidity();
		this.postCodeControl.updateValueAndValidity();
	}

	private handleIndividualCustomerControls(): void {
		this.userDetailsForm.addControl(
			UserDataFormControls.FirstName,
			this.fb.control<string>('', [Validators.required, Validators.maxLength(40)])
		);
		this.userDetailsForm.addControl(
			UserDataFormControls.LastName,
			this.fb.control<string>('', [Validators.required, Validators.maxLength(40)])
		);
		this.userDetailsForm.addControl(UserDataFormControls.PersonalIdNumber, this.fb.control<string>(''));
		this.userDetailsForm.removeControl(UserDataFormControls.Company);
		this.userDetailsForm.removeControl(UserDataFormControls.VatNumber);
	}
	private handleCompanyCustomerControls(): void {
		this.userDetailsForm.addControl(UserDataFormControls.VatNumber, this.fb.control<string>('', [Validators.required]));
		this.userDetailsForm.addControl(
			UserDataFormControls.Company,
			this.fb.control<string>('', [Validators.required, Validators.maxLength(40)])
		);
		this.userDetailsForm.removeControl(UserDataFormControls.FirstName);
		this.userDetailsForm.removeControl(UserDataFormControls.LastName);
		this.userDetailsForm.removeControl(UserDataFormControls.PersonalIdNumber);
	}
}
